
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowRight, 
  ArrowLeft,
  ArrowLeftRight,
  CreditCard, 
  Wallet, 
  PiggyBank, 
  Zap, 
  Clock, 
  ShieldCheck, 
  Sparkles, 
  Percent, 
  Gift,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import pienlainatPromoImage from "@/assets/pienlainat-promo.jpg";

interface PienlainatPromotionProps {
  variant?: "default" | "compact" | "inline";
  className?: string;
}

export const PienlainatPromotion = ({ variant = "default", className = "" }: PienlainatPromotionProps) => {
  const isMobile = useIsMobile();
  
  // Different layouts based on the variant
  if (variant === "compact") {
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
        className={className}
      >
        <Card className="border border-blue-100 overflow-hidden shadow-md hover:shadow-xl transition-all h-full relative">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-3">
            <div className="flex justify-between items-center">
              <h3 className="text-white font-medium flex items-center text-sm sm:text-base">
                <Sparkles className="h-4 w-4 text-yellow-200 mr-2" />
                <span>Pienlainat & Luottokortit</span>
              </h3>
            </div>
          </div>
          
          <CardContent className="p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-purple-50/50 flex flex-col h-full">
            <div className="mb-3 flex-grow">
              <p className="text-xs sm:text-sm text-gray-700">
                Vertaile vaihtoehtoja nopeisiin rahoitustarpeisiin - löydä edullisin ratkaisu!
              </p>
            </div>
            
            <div className="flex flex-wrap justify-center gap-1 sm:gap-2 mb-3">
              <div className="flex items-center text-xs bg-blue-100 py-0.5 px-2 rounded-full text-blue-700">
                <Wallet className="h-3 w-3 mr-1" />
                <span>20€-2000€</span>
              </div>
              <div className="flex items-center text-xs bg-purple-100 py-0.5 px-2 rounded-full text-purple-700">
                <Clock className="h-3 w-3 mr-1" />
                <span>Päätös heti</span>
              </div>
              <div className="flex items-center text-xs bg-green-100 py-0.5 px-2 rounded-full text-green-700">
                <Zap className="h-3 w-3 mr-1" />
                <span>Rahat heti</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between gap-2">
              <Link to="/lainat/kulutusluotto" className="flex-shrink-0">
                <Button variant="outline" size="sm" className="h-8 px-2">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </Link>
              
              <Link to="/lainat/pienlainat-luottokortit" className="flex-grow">
                <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-sm hover:shadow text-xs sm:text-sm py-2 h-auto">
                  Katso tarjoukset <ArrowRight className="ml-1 h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              </Link>
              
              <Link to="/lainat/joustoluotto" className="flex-shrink-0">
                <Button variant="outline" size="sm" className="h-8 px-2">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }
  
  if (variant === "inline") {
    return (
      <motion.div 
        initial={{ opacity: 0.9, y: 5 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={`flex items-center bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-lg border border-blue-100 shadow-sm overflow-hidden ${className}`}
      >
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 py-3 px-4 hidden sm:flex items-center justify-center">
          <CreditCard className="h-6 w-6 text-white" />
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between flex-grow p-3 sm:py-2 sm:px-4 gap-2 sm:gap-4">
          <div className="flex items-center">
            <CreditCard className="h-4 w-4 text-blue-600 flex-shrink-0 sm:hidden mr-2" />
            <div>
              <p className="font-medium text-blue-700 text-sm">Tarvitsetko pienempää lainaa?</p>
              <p className="text-xs text-blue-600">Vertaile pienlainoja ja luottokortteja</p>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Link to="/lainat/kulutusluotto" className="sm:hidden">
              <Button variant="outline" size="sm" className="h-8 px-2">
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </Link>
            
            <Link to="/lainat/pienlainat-luottokortit" className="w-full sm:w-auto">
              <Button size="sm" className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-xs">
                Katso tarjoukset <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </Link>
            
            <Link to="/lainat/joustoluotto" className="sm:hidden">
              <Button variant="outline" size="sm" className="h-8 px-2">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>
    );
  }

  // Default variant with more content and enhanced visuals
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={className}
    >
      <Card className="border border-blue-200 overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 h-full">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-4 sm:p-5 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img 
              src={pienlainatPromoImage} 
              alt="Pienlainat ja luottokortit" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mt-10 -mr-10 blur-2xl"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -mb-10 -ml-10 blur-xl"></div>
          
          <div className="relative z-10 flex items-center justify-between">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold">Pienlainat ja Luottokortit</h3>
              <p className="text-blue-100 mt-1 flex items-center text-sm sm:text-base">
                <Zap className="h-4 w-4 mr-1" />
                Nopeat lainat äkillisiin tarpeisiin
              </p>
            </div>
            <motion.div 
              whileHover={{ rotate: 10, scale: 1.1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              className="hidden md:flex items-center justify-center bg-white/20 h-16 w-16 rounded-full"
            >
              <CreditCard className="h-8 w-8 text-white" />
            </motion.div>
          </div>
        </div>
        
        <CardContent className="p-4 sm:p-5 bg-gradient-to-br from-white to-blue-50/50 flex flex-col h-full">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 sm:mb-5">
            <motion.div 
              whileHover={{ scale: 1.03 }}
              className="flex items-center space-x-3 bg-white p-3 rounded-lg border border-blue-100 shadow-sm"
            >
              <div className="bg-blue-100 p-2 rounded-full">
                <Wallet className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-blue-700 text-sm">Pienlainat</h4>
                <p className="text-xs text-gray-600">20€-2000€</p>
              </div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.03 }}
              className="flex items-center space-x-3 bg-white p-3 rounded-lg border border-purple-100 shadow-sm"
            >
              <div className="bg-purple-100 p-2 rounded-full">
                <CreditCard className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-purple-700 text-sm">Luottokortit</h4>
                <p className="text-xs text-gray-600">Joustava luotto</p>
              </div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.03 }}
              className="flex items-center space-x-3 bg-white p-3 rounded-lg border border-green-100 shadow-sm"
            >
              <div className="bg-green-100 p-2 rounded-full">
                <Percent className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-green-700 text-sm">Edullisin</h4>
                <p className="text-xs text-gray-600">Vertaile ehtoja</p>
              </div>
            </motion.div>
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-4">
            <div className="flex items-center text-xs sm:text-sm bg-blue-50 py-1 px-3 rounded-full text-blue-700 border border-blue-100">
              <Clock className="h-3.5 w-3.5 mr-1" />
              <span>Nopea päätös</span>
            </div>
            <div className="flex items-center text-xs sm:text-sm bg-green-50 py-1 px-3 rounded-full text-green-700 border border-green-100">
              <ShieldCheck className="h-3.5 w-3.5 mr-1" />
              <span>Turvallinen prosessi</span>
            </div>
            <div className="flex items-center text-xs sm:text-sm bg-purple-50 py-1 px-3 rounded-full text-purple-700 border border-purple-100">
              <Gift className="h-3.5 w-3.5 mr-1" />
              <span>Useita tarjouksia</span>
            </div>
          </div>
          
          <div className="mt-auto pt-2 space-y-3">
            <Link to="/lainat/pienlainat-luottokortit">
              <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium py-2 sm:py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 h-auto text-sm sm:text-base">
                Vertaile pienlainoja ja luottokortteja <ArrowRight className="ml-1 sm:ml-2 h-4 w-4 animate-pulse" />
              </Button>
            </Link>
            
            <div className="flex items-center justify-between gap-2">
              <Link to="/lainat/kulutusluotto" className="flex-1">
                <Button variant="outline" size="sm" className="w-full">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Kulutusluotot
                </Button>
              </Link>
              
              <Link to="/lainat/joustoluotto" className="flex-1">
                <Button variant="outline" size="sm" className="w-full">
                  Joustoluotot <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
            
            <div className="text-center mt-2">
              <Link to="/kilpailuta" className="text-blue-600 hover:text-blue-800 text-xs">
                <ArrowLeftRight className="inline h-3 w-3 mr-1" /> Vertaile kaikkia lainoja
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PienlainatPromotion;

